import { getCookie, setCookie } from '../../../main/js/es6/utilities/cookie-access'

export const ConsentStateEnum = {
  DEFAULT: 'default',
  UPDATE: 'update'
}

export const ConsentStorageEnum = {
  GRANTED: 'granted',
  DENIED: 'denied'
}

// Internal Namespace for cookie consent
export const CookieConsentNamesEnum = {
  CONSENT_STATUS: 'consent_status',
  CONSENT_AD_STORAGE: 'consent_ad_storage',
  CONSENT_ANALYTICS_STORAGE: 'consent_analytics_storage',
  CONSENT_FUNCTIONALITY_STORAGE: 'consent_functionality_storage',
  CONSENT_PERSONALIZATION_STORAGE: 'consent_personalization_storage',
  CONSENT_AD_USER_DATA: 'consent_ad_user_data',
  CONSENT_AD_PERSONALIZATION: 'consent_ad_personalization'
}

// Consent storages state Enums
const ConsentStateMutationEnum = {
  CONSENT_AD_STORAGE: 'adStorage',
  CONSENT_ANALYTICS_STORAGE: 'analyticsStorage',
  CONSENT_FUNCTIONALITY_STORAGE: 'functionalityStorage',
  CONSENT_PERSONALIZATION_STORAGE: 'personalizationStorage',
  CONSENT_AD_USER_DATA: 'adUserData',
  CONSENT_AD_PERSONALIZATION: 'adPersonalization'
}

export const ConsentOptionsEnum = {
  CONSENT_STATUS: 'CONSENT_STATUS',
  CONSENT_AD_STORAGE: 'CONSENT_AD_STORAGE',
  CONSENT_ANALYTICS_STORAGE: 'CONSENT_ANALYTICS_STORAGE',
  CONSENT_FUNCTIONALITY_STORAGE: 'CONSENT_FUNCTIONALITY_STORAGE',
  CONSENT_PERSONALIZATION_STORAGE: 'CONSENT_PERSONALIZATION_STORAGE',
  CONSENT_AD_USER_DATA: 'CONSENT_AD_USER_DATA',
  CONSENT_AD_PERSONALIZATION: 'CONSENT_AD_PERSONALIZATION'
}

export default {
  namespaced: true,
  state: {
    cookieNoticeVisible: true,
    cookieSettingsVisible: false,

    // Enables storage, such as cookies, related to advertising
    adStorage: {
      defaultValue: ConsentStorageEnum.DENIED,
      validForDays: 365,
      value: null
    },
    // Enables storage, such as cookies, related to analytics (for example, visit duration)
    analyticsStorage: {
      defaultValue: ConsentStorageEnum.DENIED,
      validForDays: 365,
      value: null
    },
    // Enables storage that supports the functionality of the website or app such as language settings
    functionalityStorage: {
      defaultValue: ConsentStorageEnum.GRANTED,
      validForDays: 365,
      value: null
    },
    // Enables storage related to personalization such as video recommendations
    personalizationStorage: {
      defaultValue: ConsentStorageEnum.DENIED,
      validForDays: 365,
      value: null
    },
    // Sets consent for sending user data related to advertising to Google
    adUserData: {
      defaultValue: ConsentStorageEnum.DENIED,
      validForDays: 365,
      value: null
    },
    // Sets consent for personalized advertising.
    adPersonalization: {
      defaultValue: ConsentStorageEnum.DENIED,
      validForDays: 365,
      value: null
    }
  },
  getters: {
    getConsentByName: (state) => (consentName) => {
      return state[consentName]
    },
    getCookieNoticeVisible (state) {
      return state.cookieNoticeVisible
    },
    getCookieSettingsVisible (state) {
      return state.cookieSettingsVisible
    },
    getCookieConsentStatus () {
      return getCookie(CookieConsentNamesEnum.CONSENT_STATUS)
    },
    getAdStorage (state) {
      return state.adStorage
    },
    getAnalyticsStorage (state) {
      return state.analyticsStorage
    },
    getFunctionalityStorage (state) {
      return state.functionalityStorage
    },
    getPersonalizationStorage (state) {
      return state.personalizationStorage
    },
    getAdUserData (state) {
      return state.adUserData
    },
    getAdPersonalization (state) {
      return state.adPersonalization
    }
  },
  mutations: {
    setNoticeVisibility (state, isVisible) {
      state.cookieNoticeVisible = isVisible
    },
    setSettingsVisibility (state, isVisible) {
      state.cookieSettingsVisible = isVisible
    },
    updateConsentStateByName (state, {
      consentName,
      newConsentState
    }) {
      const consentStateName = ConsentStateMutationEnum[consentName]
      state[consentStateName] = Object.assign(state[consentStateName], newConsentState)
    }
  },
  actions: {
    openSettings ({ commit }) {
      commit('setNoticeVisibility', false)
      commit('setSettingsVisibility', true)
    },
    async setConsentValueByName ({
      commit,
      getters,
      dispatch
    }, {
      consentName,
      consentValue
    }) {
      commit('updateConsentStateByName', {
        consentName: consentName,
        newConsentState: {
          value: consentValue
        }
      })
    },
    async updateCookie ({
      getters,
      dispatch
    }, consentName) {
      const cookieConsentName = CookieConsentNamesEnum[consentName]
      const stateConsentName = ConsentStateMutationEnum[consentName] || false
      const consent = (getters.getConsentByName(stateConsentName))
      await setCookie(cookieConsentName, consent.value, consent.validForDays)
    },
    async updateGtagConsentFromState ({ getters }) {
      window.dataLayer = window.dataLayer || []

      function gtag () {
        window.dataLayer.push(arguments)
      }

      gtag('consent', ConsentStateEnum.UPDATE, {
        ad_storage: (getters.getConsentByName(ConsentStateMutationEnum.CONSENT_AD_STORAGE)).value || ConsentStorageEnum.DENIED,
        analytics_storage: (getters.getConsentByName(ConsentStateMutationEnum.CONSENT_ANALYTICS_STORAGE)).value || ConsentStorageEnum.DENIED,
        functionality_storage: (getters.getConsentByName(ConsentStateMutationEnum.CONSENT_FUNCTIONALITY_STORAGE)).value || ConsentStorageEnum.GRANTED,
        personalization_storage: (getters.getConsentByName(ConsentStateMutationEnum.CONSENT_PERSONALIZATION_STORAGE)).value || ConsentStorageEnum.DENIED,
        ad_user_data: (getters.getConsentByName(ConsentStateMutationEnum.CONSENT_AD_USER_DATA)).value || ConsentStorageEnum.DENIED,
        ad_personalization: (getters.getConsentByName(ConsentStateMutationEnum.CONSENT_AD_PERSONALIZATION)).value || ConsentStorageEnum.DENIED
        // security_storage is not supported for our platform
      })
    },
    async setAllConsentValuesToGranted ({ dispatch }) {
      await Promise.all([
        dispatch('setConsentValueByName', {
          consentName: ConsentOptionsEnum.CONSENT_AD_STORAGE,
          consentValue: ConsentStorageEnum.GRANTED
        }),
        dispatch('setConsentValueByName', {
          consentName: ConsentOptionsEnum.CONSENT_ANALYTICS_STORAGE,
          consentValue: ConsentStorageEnum.GRANTED
        }),
        dispatch('setConsentValueByName', {
          consentName: ConsentOptionsEnum.CONSENT_FUNCTIONALITY_STORAGE,
          consentValue: ConsentStorageEnum.GRANTED
        }),
        dispatch('setConsentValueByName', {
          consentName: ConsentOptionsEnum.CONSENT_PERSONALIZATION_STORAGE,
          consentValue: ConsentStorageEnum.GRANTED
        }),
        dispatch('setConsentValueByName', {
          consentName: ConsentOptionsEnum.CONSENT_AD_USER_DATA,
          consentValue: ConsentStorageEnum.GRANTED
        }),
        dispatch('setConsentValueByName', {
          consentName: ConsentOptionsEnum.CONSENT_AD_PERSONALIZATION,
          consentValue: ConsentStorageEnum.GRANTED
        })
      ])
    },
    async setConsentValuesFromState ({ dispatch, getters }) {
      await Promise.all([
        dispatch('setConsentValueByName', {
          consentName: ConsentOptionsEnum.CONSENT_AD_STORAGE,
          consentValue: getters.getAdStorage.value || getters.getAdStorage.defaultValue
        }),
        dispatch('setConsentValueByName', {
          consentName: ConsentOptionsEnum.CONSENT_ANALYTICS_STORAGE,
          consentValue: getters.getAnalyticsStorage.value || getters.getAnalyticsStorage.defaultValue
        }),
        dispatch('setConsentValueByName', {
          consentName: ConsentOptionsEnum.CONSENT_FUNCTIONALITY_STORAGE,
          consentValue: getters.getFunctionalityStorage.value || getters.getFunctionalityStorage.defaultValue
        }),
        dispatch('setConsentValueByName', {
          consentName: ConsentOptionsEnum.CONSENT_PERSONALIZATION_STORAGE,
          consentValue: getters.getPersonalizationStorage.value || getters.getPersonalizationStorage.defaultValue
        }),
        dispatch('setConsentValueByName', {
          consentName: ConsentOptionsEnum.CONSENT_AD_USER_DATA,
          consentValue: getters.getAdUserData.value || getters.getAdUserData.defaultValue
        }),
        dispatch('setConsentValueByName', {
          consentName: ConsentOptionsEnum.CONSENT_AD_PERSONALIZATION,
          consentValue: getters.getAdPersonalization.value || getters.getAdPersonalization.defaultValue
        })
      ])
    },
    async updateAllCookiesFromState ({ dispatch }) {
      await Promise.all([
        dispatch('updateCookie', ConsentOptionsEnum.CONSENT_AD_STORAGE),
        dispatch('updateCookie', ConsentOptionsEnum.CONSENT_ANALYTICS_STORAGE),
        dispatch('updateCookie', ConsentOptionsEnum.CONSENT_FUNCTIONALITY_STORAGE),
        dispatch('updateCookie', ConsentOptionsEnum.CONSENT_PERSONALIZATION_STORAGE),
        dispatch('updateCookie', ConsentOptionsEnum.CONSENT_AD_USER_DATA),
        dispatch('updateCookie', ConsentOptionsEnum.CONSENT_AD_PERSONALIZATION)
      ])
    }
  }
}
